<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { onDestroy, onMount } from 'svelte';
  import { fade, slide } from 'svelte/transition';
  import Text from '../../typography/Text.svelte';
  import { Checkout } from '../../../services/checkout';
  import { Container } from '../../../services/container';
  import AnimatedCoin from '../../misc/AnimatedCoin.svelte';
  import { AppState, SetNotification } from '../../../etc/stores';
  import { sleep } from '../../../etc/utils';
  import { replaceVariables } from '../../../i18n';

  const checkout = Container.get(Checkout);

  let progress = 0;
  let completed = false;
  let success = false;
  let interval;

  const t = (text: string, values?: any) => {
    return replaceVariables($_(`postcheckout_loading.${text}`), values);
  };

  const close = () => {
    SetNotification('', true);
  };

  const startLoading = () => {
    interval = setInterval(() => {
      if (progress <= 80) {
        progress += 1;
      }
    }, 200);
  };

  const handleCheckout = async () => {
    const transaction = await $AppState.checkout.checkoutHandleFunction();

    if (transaction) {
      completed = true;

      if (progress < 30) {
        progress = 30;
        await sleep(2000);
      }

      progress = 100;

      await sleep(2000);

      success = true;

      await sleep(2750);

      await checkout.onCheckoutSuccess(transaction);

      if ($AppState.website.category === 'postCheckoutLoading') {
        close();
      } else {
        AppState.update(state => ({
          ...state,
          website: {
            ...state.website,
            showCollapsedIcon: true
          },
          checkout: {
            ...state.checkout,
            checkoutHandleFunction: null
          }
        }));
      }
    }
  };

  onDestroy(() => {
    AppState.update(state => ({
      ...state,
      website: {
        ...state.website,
        showCollapsedIcon: true
      },
      checkout: {
        ...state.checkout,
        checkoutHandleFunction: null
      }
    }));

    clearInterval(interval);
  });

  onMount(() => {
    startLoading();
    setTimeout(() => {
      handleCheckout();
    }, 2000);
  });
</script>

<div
  class="twism-post-checkout-loading"
  in:slide={{ duration: 800, delay: 600 }}
>
  {#if success}
    <div class="twism-post-checkout-loading--success" in:fade>
      <img
        src="{process.env.BASE_URL}/images/check-2.svg"
        alt="Success check icon"
      />
    </div>
  {/if}
  <div class="twism-post-checkout-loading--brand">
    <AnimatedCoin
      image={$AppState.business.logo.publicPath.large}
      scale="0.7"
    />
  </div>
  <div class="twism-post-checkout-loading--content">
    <div class="twism-post-checkout-loading--texts">
      {#if progress < 30}
        <div
          class="twism-post-checkout-loading--text-container"
          transition:fade
        >
          <Text class="twism-post-checkout-loading--text" type="medium">
            {t('first-loading-phrase')}
          </Text>
        </div>
      {:else if progress < 100}
        <div
          class="twism-post-checkout-loading--text-container"
          transition:fade
        >
          <Text class="twism-post-checkout-loading--text" type="medium">
            {t('second-loading-phrase')}
          </Text>
        </div>
      {:else}
        <div
          class="twism-post-checkout-loading--text-container"
          transition:fade
        >
          <Text class="twism-post-checkout-loading--text" type="medium">
            {t('third-loading-phrase')}
          </Text>
        </div>
      {/if}
    </div>
    <div class="twism-post-checkout-loading--progress-bar">
      <div
        class="twism-post-checkout-loading--progress-bar--track {completed
          ? '-twism-completed'
          : ''}"
        style="width: {progress}%"
      />
    </div>
  </div>
</div>

<style lang="scss">
  @use './styles.scss' as *;
  .twism-post-checkout-loading {
    width: 100%;
    height: 88px;
    background: var(--twism-theme-primary600);
    box-shadow: 0px -2px 48px -13px rgba(4, 33, 67, 0.14);
    display: flex;
    align-items: center;
    overflow: hidden;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 100;
    padding: 12px 16px;
    box-sizing: border-box;

    &--success {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: #39b99f;
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 101;
    }

    &--brand {
      height: 100%;
      min-width: 64px;
      background: var(--twism-theme-primary600);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &--content {
      width: 100%;
      height: 100%;
      display: flex;
      flex-flow: column nowrap;
      justify-content: center;
      box-sizing: border-box;
      padding-left: 16px;
    }

    &--texts {
      position: relative;
    }

    &--text-container {
      position: absolute;
    }

    &--progress-bar {
      width: 100%;
      height: 8px;
      border-radius: 20px;
      background: var(--twism-theme-primary500);
      margin-top: 28px;
      position: relative;

      &--track {
        border-radius: 20px;
        height: 100%;
        position: absolute;
        background: var(--twism-theme-backgroundGradient);
        bottom: 0;
        left: 0;
        top: 0;
        transition: all 250ms ease-in-out;

        &.-twism-completed {
          transition: all 0.5s ease-in-out;
        }
      }
    }
  }
</style>
